// Active-filter pill strip rendered above every scoped page. Reads the
// shell's `scope` object and emits one removable pill per dimension, with
// per-key labels. metaType is
// intentionally excluded as it drives the MetaListPage pivot, not a
// filter.
//
// The strip groups chips by dimension and renders the combine rule
// literally: chips inside one dimension are joined by `OR`, dimensions
// are joined by `AND`. This mirrors the actual filter semantics (values
// inside a dimension are ORed; dimensions are ANDed) so admins can see
// how their stacked filters compose without guessing.
//
// Calculations: none. Pure presentation over the scope shape owned by
// index.js.
//
// Postgres mapping: scope keys mirror columns on `users`
// (department, location, city, country, state, company, ad_group,
// manager) plus a few sim/course dimensions (commsType, course). When
// scope filtering moves server-side, this same key set drives the WHERE
// clause builder.
//
// Integration hook: no API surface. If scope ever becomes a saved-view
// concept, this is the canonical key list to serialise.
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { TAG_LABELS } from './../tagAnalytics';
import { ReportFiltersContext } from '../reportFilterStore';

// Scope keys that we surface as removable filter pills. metaType is used internally
// for meta-list pivots and is intentionally excluded.
const SCOPE_LABELS = {
    department: 'Department',
    manager:    'Line manager',
    location:   'Location',
    city:       'City',
    country:    'Country',
    company:    'Company',
    state:      'State',
    commsType:  'Comms type',
    course:     'Course',
    sim:        'Simulation',
    user:       'User',
    tag:        'Lure tag',
    tenancy:    'Tenancy',
};

// tenancy values are numeric ids; resolve to the tenancy name via the redux
// lookup (populated by the header switcher), falling back to the id itself.
const renderValue = (key, value, tenancyLookup) => {
    if (key === 'tag') return TAG_LABELS[value] || value;
    if (key === 'tenancy') return (tenancyLookup && tenancyLookup[value]) || value;
    return value;
};

function Chip({ scopeKey, value, removeScope, tenancyLookup }) {
    const display = renderValue(scopeKey, value, tenancyLookup);
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                padding: '4px 4px 4px 10px',
                borderRadius: 4,
                border: '1px solid var(--teal)',
                background: 'transparent',
                fontFamily: 'Inter, sans-serif',
            }}
        >
            <span style={{ color: 'var(--text)', fontWeight: 600 }}>{display}</span>
            <button
                onClick={() => removeScope(scopeKey, value)}
                aria-label={`Remove ${SCOPE_LABELS[scopeKey]}: ${display} filter`}
                title={`Remove ${SCOPE_LABELS[scopeKey]}: ${display} filter`}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--teal)',
                    cursor: 'pointer',
                    fontSize: 13,
                    lineHeight: 1,
                    padding: 0,
                    transition: 'background .15s, color .15s',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--teal)';
                    e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--teal)';
                }}
            >
                x
            </button>
        </span>
    );
}

const operatorStyle = (variant) => ({
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: variant === 'and' ? 'var(--teal)' : 'var(--muted)',
    background: variant === 'and' ? 'rgba(0,128,128,0.08)' : 'transparent',
    border: variant === 'and' ? '1px solid var(--teal)' : '1px dashed var(--border)',
    borderRadius: 4,
    padding: '2px 6px',
    fontFamily: 'Inter, sans-serif',
    textTransform: 'uppercase',
});

export default function ActiveFilters({ scope, removeScope, noPadX = false }) {
    const tenancyLookup = useSelector((state) => state.tenancy.tenancyLookup);
    const reportFilters = useContext(ReportFiltersContext);
    const groups = Object.entries(scope || {})
        .filter(([k]) => SCOPE_LABELS[k])
        .map(([type, values]) => ({
            type,
            values: Array.isArray(values) ? values : [values],
        }))
        .filter((g) => g.values.length > 0);

    if (groups.length === 0) return null;

    const totalPairs = groups.reduce((n, g) => n + g.values.length, 0);
    const canReset = typeof reportFilters?.resetFilters === 'function';
    const clearAll = () => {
        if (canReset) reportFilters.resetFilters();
        else groups.forEach((g) => g.values.forEach((v) => removeScope(g.type, v)));
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                padding: noPadX ? '0 0 8px' : '0 24px 8px',
            }}
        >
            <span
                style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--dim)',
                }}
            >
                Filters
            </span>

            {groups.map((g, gi) => (
                <React.Fragment key={g.type}>
                    {gi > 0 && (
                        <span style={operatorStyle('and')} aria-label="and">AND</span>
                    )}
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 8px',
                            borderRadius: 6,
                            border: '1px dashed var(--border)',
                            background: 'transparent',
                        }}
                    >
                        <span
                            style={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: 'var(--muted)',
                                marginRight: 2,
                            }}
                        >
                            {SCOPE_LABELS[g.type]}
                        </span>
                        {g.values.map((v, vi) => (
                            <React.Fragment key={`${g.type}:${v}`}>
                                {vi > 0 && <span style={operatorStyle('or')} aria-label="or">OR</span>}
                                <Chip scopeKey={g.type} value={v} removeScope={removeScope} tenancyLookup={tenancyLookup} />
                            </React.Fragment>
                        ))}
                    </span>
                </React.Fragment>
            ))}

            {(totalPairs > 1 || (canReset && !reportFilters.isDefault)) && (
                <button
                    onClick={clearAll}
                    style={{
                        fontSize: 10,
                        fontWeight: 500,
                        padding: '3px 8px',
                        borderRadius: 999,
                        border: '1px solid transparent',
                        background: 'transparent',
                        color: 'var(--muted)',
                        fontFamily: 'Inter, sans-serif',
                        cursor: 'pointer',
                        transition: 'color .15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--muted)'; }}
                >
                    Clear all
                </button>
            )}
        </div>
    );
}
